import React from 'react';
import { WxbSegmented } from '../Segmented/Segmented';
import { WxbTabs } from '../Tabs/Tabs';
import './ContentSwitch.css';

export interface WxbContentSwitchItem {
  key: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
}

export interface WxbContentSwitchProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: WxbContentSwitchItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  variant?: 'segmented' | 'tabs';
  size?: 'sm' | 'md';
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

const firstEnabledKey = (items: WxbContentSwitchItem[]) => (
  items.find((item) => !item.disabled)?.key ?? ''
);

export const WxbContentSwitch: React.FC<WxbContentSwitchProps> = ({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  variant = 'segmented',
  size = 'md',
  className = '',
  'aria-label': ariaLabel = '内容切换',
  ...props
}) => {
  const initialKey = items.some((item) => item.key === defaultActiveKey && !item.disabled)
    ? defaultActiveKey!
    : firstEnabledKey(items);
  const [internalKey, setInternalKey] = React.useState(initialKey);
  const requestedKey = activeKey ?? internalKey;
  const currentKey = items.some((item) => item.key === requestedKey && !item.disabled)
    ? requestedKey
    : firstEnabledKey(items);
  const activeItem = items.find((item) => item.key === currentKey);

  React.useEffect(() => {
    if (activeKey !== undefined || internalKey === currentKey) return;
    setInternalKey(currentKey);
  }, [activeKey, currentKey, internalKey]);

  const select = (key: string) => {
    const item = items.find((candidate) => candidate.key === key);
    if (!item || item.disabled) return;
    if (activeKey === undefined) setInternalKey(key);
    onChange?.(key);
  };

  const renderContent = (item: WxbContentSwitchItem | undefined) => (
    <>
      {item?.description && (
        <div className="wxb-content-switch-description">{item.description}</div>
      )}
      <div className="wxb-content-switch-panel">
        {item?.children}
      </div>
    </>
  );

  return (
    <div className={joinClasses('wxb-content-switch', `wxb-content-switch-${variant}`, className)} {...props}>
      {variant === 'segmented' ? (
        <>
          <div className="wxb-content-switch-control">
          <WxbSegmented
            size={size}
            value={currentKey}
            onChange={select}
            aria-label={ariaLabel}
            options={items.map((item) => ({
              label: item.label,
              value: item.key,
              disabled: item.disabled,
            }))}
          />
          </div>
          {renderContent(activeItem)}
        </>
      ) : (
        <WxbTabs
          className="wxb-content-switch-tabs"
          activeKey={currentKey}
          onChange={select}
          aria-label={ariaLabel}
          items={items.map((item) => ({
            key: item.key,
            disabled: item.disabled,
            label: (
              <>
                <span>{item.label}</span>
                {item.badge && <span className="wxb-content-switch-tab-badge">{item.badge}</span>}
              </>
            ),
            children: renderContent(item),
          }))}
        />
      )}
    </div>
  );
};
